import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { crmSupabase } from "../../lib/crm-client";

type Lead = {
  id: string;
  full_name: string;
  status: string;
  is_estrella: boolean;
  is_test: boolean;
  last_contact_at: string | null;
  category: { name: string; color_hex: string; slug: string } | null;
};

export default function CrmLeads() {
  const [rows, setRows] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    if (!crmSupabase) {
      setLoading(false);
      return;
    }
    crmSupabase
      .from("lead")
      .select(
        "id,full_name,status,is_estrella,is_test,last_contact_at,category:category_id(name,color_hex,slug)",
      )
      .order("is_estrella", { ascending: false })
      .order("full_name", { ascending: true })
      .then(({ data, error }) => {
        if (error) console.error(error);
        setRows((data as any[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showTest && r.is_test) return false;
      if (q && !r.full_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, showTest]);

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1200px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  return (
    <main className="px-10 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
          Leads
        </h2>
        <span className="font-mono text-xs uppercase text-concrete">
          {filtered.length} sur {rows.length} ·{" "}
          {filtered.filter((r) => r.is_estrella).length} Estrellas
        </span>
      </header>

      <div className="flex gap-3 mb-4">
        <input
          type="text"
          placeholder="Rechercher un nom…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 bg-white border border-stone rounded text-sm font-mono focus:outline-none focus:border-charcoal"
        />
        <label className="flex items-center gap-2 font-mono text-xs uppercase text-concrete">
          <input
            type="checkbox"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
            className="w-4 h-4"
          />
          Show test users
        </label>
      </div>

      <table className="w-full bg-white border border-stone rounded-lg overflow-hidden">
        <thead className="bg-stone text-charcoal font-mono text-[10px] uppercase">
          <tr>
            <th className="text-left px-3 py-2">Nom</th>
            <th className="text-left px-3 py-2">Catégorie</th>
            <th className="text-left px-3 py-2">Statut</th>
            <th className="text-left px-3 py-2">Dernier contact</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.id} className="border-t border-stone hover:bg-ivory">
              <td className="px-3 py-2 text-sm">
                <Link to={`/crm/leads/${r.id}`} className="font-semibold text-charcoal hover:underline">
                  {r.is_estrella && <span className="text-lemonade mr-1">★</span>}
                  {r.full_name}
                </Link>
                {r.is_test && (
                  <span className="ml-2 font-mono text-[9px] uppercase text-ash">test</span>
                )}
              </td>
              <td className="px-3 py-2">
                {r.category && (
                  <span
                    className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded text-white"
                    style={{ backgroundColor: r.category.color_hex }}
                  >
                    {r.category.name}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 font-mono text-[10px] uppercase text-concrete">
                {r.status}
              </td>
              <td className="px-3 py-2 font-mono text-[10px] text-concrete">
                {r.last_contact_at?.slice(0, 10) ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
